import React, { useEffect, useState } from "react";
import { Flex, Typography, Button, Dropdown, Menu, Tooltip } from "antd";
import SegmentDetail from "./SegmentDetail";
import SegmentNavigator from "./SegmentNavigator";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { themeState } from "../lib/atom";
import { DownOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import deleteButtonIcon from "../assets/ui_revamp/DeleteButton.svg";
import chevronUpIcon from "../assets/ui_revamp/Chevron Up.svg";
import sphereIcon from "../assets/ui_revamp/Sphere.svg";
import {
  deleteSegmentIndex,
  panelSettings,
  polygonsState,
  selectedIndicesState,
} from "../ThreeJs/ReactStates/states";
import { selectSegment, hoverSegmentByIndex, clearSegmentHover } from "../ThreeJs/Segments/Functions";
import { selectObstacle } from "../ThreeJs/Obstacles/ObstacleFunctions";
import { updatePolygonDetailsSelector } from "../ThreeJs/ReactStates/updates";
import { DrawingModes, POLYGONS, TREES, VARS } from "../ThreeJs/Global/variables";
import { pushUndoAction } from "../ThreeJs/Global/UndoManager";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { enableTreeDrawing } from "../ThreeJs/Trees/enableTreeDrawing.js";
import { notifyIrradianceNeedsRecompute } from "../ThreeJs/Shading/IrradianceEvents";
import RemovedSections from "./RemovedSections";
import TreeEditorSegment from "./TreeEditorSegment";
import { toast } from "../lib/toast";

const Sections = ({
  updateObstacleType,
  updateDrawingMode,
  currentDrawingMode,
  setDeleteConfirmationVisible,
  panelList,
  removedSections,
  setRemovedSections,
  activePanel,
  selectedTrimSphere,
  selectedTree,
}) => {
  const theme = useRecoilValue(themeState);
  const polygonState = useRecoilValue(polygonsState);
  const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState);
  const setDeleteSegment = useSetRecoilState(deleteSegmentIndex);
  const setPolygonDetails = useSetRecoilState(updatePolygonDetailsSelector);
  const [showPanelSettings, setShowPanelSettings] = useRecoilState(panelSettings);
  const [treeShape, setTreeShape] = useState("cone");
  const [trees, setTrees] = useState(() => [...(TREES?.trees || [])]);
  const [irradianceBusy, setIrradianceBusy] = useState(
    typeof window !== "undefined" && window.isIrradianceBusy === true
  );
  const PANEL_M2_TO_SQFT = 10.7639;

  const getPanelAreaSqft = (panel) => {
    if (panel?.panel?.geometry?.parameters) {
      const { width, height } = panel.panel.geometry.parameters; // meters
      if (typeof width === "number" && typeof height === "number") {
        return width * height * PANEL_M2_TO_SQFT;
      }
    }

    if (typeof panel?.area === "number") {
      // Heuristic: small = m2, large = ft2 (for compatibility with existing code)
      if (panel.area < 30) {
        return panel.area * PANEL_M2_TO_SQFT;
      }
      return panel.area;
    }

    return 0;
  };

  const handleClick = (event) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (!showPanelSettings) {
      if (currentDrawingMode === DrawingModes.PANEL) {
        updateDrawingMode(DrawingModes.NONE);
      }
    } else if (
      selectedIndices.segmentIndex !== null &&
      selectedIndices.obstacleIndex !== null
    ) {
      selectObstacle(
        selectedIndices.segmentIndex,
        selectedIndices,
        selectedIndices.obstacleIndex,
        setSelectedIndices,
        setPolygonDetails
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPanelSettings]);

  useEffect(() => {
    setTrees([...(TREES?.trees || [])]);
  }, [selectedTree, removedSections]);

  useEffect(() => {
    const onBusyChange = (event) => {
      setIrradianceBusy(event?.detail?.busy === true);
    };
    window.addEventListener("IRRADIANCE_BUSY_CHANGED", onBusyChange);
    return () => window.removeEventListener("IRRADIANCE_BUSY_CHANGED", onBusyChange);
  }, []);

  // Loaded trees get the same move/select/height-handle interaction as newly
  // drawn ones.  Drawing itself remains disabled until a tree tool is chosen.
  useEffect(() => {
    const { scene, camera, renderer } = sceneManager || {};
    if (!scene || !camera || !renderer) return undefined;
    const houseMeshes = Array.isArray(VARS?.Meshes) && VARS.Meshes.length
      ? VARS.Meshes
      : scene.children.filter((mesh) => mesh.name === "houseModel");
    return enableTreeDrawing(scene, camera, renderer, houseMeshes, setTrees, treeShape, {
      drawingEnabled: false,
    });
    // SceneManager is a singleton; bind once for this sidebar lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSegmentClick = (index) => {
    setShowPanelSettings(false);
    selectSegment(selectedIndices, index, setSelectedIndices, setPolygonDetails);
  };

  const handleDeleteSegment = (index) => {
    setDeleteSegment(index);
    setDeleteConfirmationVisible(true);
  };

  const highlightTree = (tree, on) => {
    if (!tree) return;
    tree.traverse((obj) => {
      if (obj.material?.emissive) {
        obj.material.emissive.set(on ? 0x444400 : 0x000000);
      }
    });
  };

  const handleTreeSelect = (tree) => {
    if (!tree) return;

    if (selectedTree === tree) {
      highlightTree(tree, false);
      if (tree.userData?.arrow) tree.userData.arrow.visible = false;

      if (
        typeof window !== "undefined" &&
        window.transformControls?.object === tree
      ) {
        try {
          window.transformControls.detach();
        } catch (e) {}
      }

      VARS.selectedTree = null;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: null } }));
      }
      return;
    }

    if (selectedTree && selectedTree !== tree) {
      highlightTree(selectedTree, false);
      if (selectedTree.userData?.arrow) selectedTree.userData.arrow.visible = false;
    }

    const isLocked = !!tree.userData?.locked;
    highlightTree(tree, true);
    if (tree.userData?.arrow) {
      tree.userData.arrow.visible = !isLocked;
    }

    if (typeof window !== "undefined" && window.attachControlsToTree) {
      window.attachControlsToTree(tree);
    }

    VARS.selectedTree = tree;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree } }));
    }
  };

  const handleDeleteTree = (treeToDelete) => {
    if (irradianceBusy) return;
    if (!treeToDelete) return;
    const idx = TREES?.trees?.indexOf(treeToDelete);
    if (idx === -1) return;
    const wasSelected = selectedTree === treeToDelete || VARS.selectedTree === treeToDelete;

    try {
      if (window.transformControls?.object === treeToDelete) {
        window.transformControls.detach();
      }
    } catch (e) {}

    treeToDelete.parent?.remove(treeToDelete);
    if (idx !== -1) TREES.trees.splice(idx, 1);

    setTrees([...(TREES?.trees || [])]);
    notifyIrradianceNeedsRecompute("tree-deleted");
    toast.success("Tree deleted");

    if (wasSelected) {
      VARS.selectedTree = null;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: null } }));
      }
    }

    pushUndoAction({
      label: "Delete tree",
      undo: () => {
        if (!sceneManager.scene.children.includes(treeToDelete)) {
          sceneManager.scene.add(treeToDelete);
        }
        if (!TREES.trees.includes(treeToDelete)) {
          TREES.trees.splice(Math.max(0, idx), 0, treeToDelete);
        }
        if (wasSelected) {
          VARS.selectedTree = treeToDelete;
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: treeToDelete } }));
          }
        }
        setTrees([...(TREES?.trees || [])]);
        notifyIrradianceNeedsRecompute("tree-deleted");
      },
      redo: () => {
        const i = TREES.trees.indexOf(treeToDelete);
        if (i !== -1) TREES.trees.splice(i, 1);
        treeToDelete.parent?.remove(treeToDelete);
        if (VARS.selectedTree === treeToDelete) {
          VARS.selectedTree = null;
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: null } }));
          }
        }
        setTrees([...(TREES?.trees || [])]);
        notifyIrradianceNeedsRecompute("tree-deleted");
      },
    });
  };

  // Keyboard deletion deliberately reuses handleDeleteTree rather than
  // duplicating removal logic in the global key listener.
  useEffect(() => {
    const onTreeDeleteRequested = (event) => {
      handleDeleteTree(event?.detail?.tree || VARS.selectedTree);
    };
    window.addEventListener("TREE_DELETE_REQUESTED", onTreeDeleteRequested);
    return () => window.removeEventListener("TREE_DELETE_REQUESTED", onTreeDeleteRequested);
  }, [handleDeleteTree]);

  const toggleTreeLock = (tree) => {
    if (irradianceBusy) return;
    if (!tree) return;
    const beforeLocked = !!tree.userData?.locked;
    const nextLocked = !tree.userData?.locked;
    tree.userData.locked = nextLocked;

    if (tree.userData?.arrow) {
      tree.userData.arrow.visible = !nextLocked;
    }

    if (
      nextLocked &&
      typeof window !== "undefined" &&
      window.transformControls?.object === tree
    ) {
      try {
        window.transformControls.detach();
      } catch (e) {}
    }

    setTrees([...(TREES?.trees || [])]);
    notifyIrradianceNeedsRecompute("tree-lock-toggled");

    pushUndoAction({
      label: "Toggle tree lock",
      undo: () => {
        tree.userData.locked = beforeLocked;
        if (tree.userData?.arrow) tree.userData.arrow.visible = !beforeLocked;
        if (beforeLocked && window.transformControls?.object === tree) {
          try {
            window.transformControls.detach();
          } catch (e) {}
        }
        setTrees([...(TREES?.trees || [])]);
        notifyIrradianceNeedsRecompute("tree-lock-toggled");
      },
      redo: () => {
        tree.userData.locked = nextLocked;
        if (tree.userData?.arrow) tree.userData.arrow.visible = !nextLocked;
        if (nextLocked && window.transformControls?.object === tree) {
          try {
            window.transformControls.detach();
          } catch (e) {}
        }
        setTrees([...(TREES?.trees || [])]);
        notifyIrradianceNeedsRecompute("tree-lock-toggled");
      },
    });
  };

  const startTreeDrawing = (shape = treeShape) => {
    if (irradianceBusy) return;
    updateDrawingMode(DrawingModes.NONE);

    const houseMeshes = sceneManager?.scene?.children.filter(
      (mesh) => mesh.name === "houseModel"
    );

    enableTreeDrawing(
      sceneManager.scene,
      sceneManager.camera,
      sceneManager.renderer,
      houseMeshes,
      setTrees,
      shape
    );
  };

  const treeMenu = (
    <Menu
      items={[
        {
          key: "cone",
          label: "Cone Tree",
          onClick: () => {
            setTreeShape("cone");
            startTreeDrawing("cone");
          },
        },
        {
          key: "circle",
          label: "Circle Tree",
          onClick: () => {
            setTreeShape("circle");
            startTreeDrawing("circle");
          },
        },
      ]}
    />
  );

  const segmentCards =
    polygonState.length > 0
      ? polygonState.map((polygon, index) => {
          const segmentName = polygon.label;
          const segmentData = POLYGONS[index] || polygon;
          const panels = segmentData?.panels || polygon.panels || [];
          const isSegmentSelected = selectedIndices.segmentIndex === index;
          const segmentTextClass = theme ? "text-gray" : "text-gray-1";

          const totalPanelsAreaSqft = panels.reduce(
            (sum, panel) => sum + getPanelAreaSqft(panel),
            0
          );
          const panelsCount = panels.length;
          const hasPanels = panelsCount > 0;

          const segmentArea = segmentData?.details?.area ?? polygon?.details?.area;
          const rawTotalAreaSqft =
            segmentArea !== null && segmentArea !== undefined
              ? parseFloat(segmentArea)
              : 0;
          const totalAreaSqft = Number.isFinite(rawTotalAreaSqft)
            ? rawTotalAreaSqft
            : 0;

          const rawSetbackArea = segmentData?.setBacks?.avaArea;
          const setbackAreaSqft =
            rawSetbackArea !== "" &&
            rawSetbackArea !== null &&
            rawSetbackArea !== undefined
              ? parseFloat(rawSetbackArea)
              : null;

          const hasSetbacks =
            !!segmentData?.setBacks?.isSet &&
            Array.isArray(segmentData?.setBacks?.edgesData) &&
            segmentData.setBacks.edgesData.some((edge) => (edge.value || 0) > 0) &&
            setbackAreaSqft !== null &&
            !Number.isNaN(setbackAreaSqft);

          const availableAfterPanelsSqft =
            !hasSetbacks && hasPanels
              ? Math.max(0, totalAreaSqft - totalPanelsAreaSqft)
              : null;

          const availableAfterSetbackAndPanelsSqft =
            hasSetbacks && hasPanels
              ? Math.max(0, setbackAreaSqft - totalPanelsAreaSqft)
              : null;

          const segmentAreaText = `${totalAreaSqft.toFixed(2)} sqft`;
          const segmentMetaText =
            panelsCount > 0
              ? `${panelsCount} panels, ${segmentAreaText}`
              : segmentAreaText;

          return (
            <React.Fragment key={index}>
              <div
                className={`segment-pill segment-pill--segment ui-card ${theme ? "dark" : ""} ${
                  isSegmentSelected ? "is-expanded" : ""
                }`}
                onMouseEnter={() => hoverSegmentByIndex(index, selectedIndices.segmentIndex)}
                onMouseLeave={() => clearSegmentHover(selectedIndices.segmentIndex)}
              >
              <Typography.Text
                strong
                className={`segment-pill-title ${segmentTextClass}`}
                role="button"
                tabIndex={0}
                aria-expanded={isSegmentSelected}
                onClick={() => handleSegmentClick(index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSegmentClick(index);
                  }
                }}
              >
                <span className="segment-pill-text">
                  <span className="segment-pill-label">Section {segmentName}</span>
                  <span className="segment-pill-subtext">{segmentMetaText}</span>
                </span>
              </Typography.Text>

              <div className="segment-pill-actions">
                <Button
                  danger
                  type={"text"}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteSegment(index);
                  }}
                  className="delete-button"
                  icon={
                    <img
                      src={deleteButtonIcon}
                      alt="Delete"
                      className="delete-icon-img"
                    />
                  }
                />
                <img
                  src={chevronUpIcon}
                  alt="Toggle section"
                  className={`segment-pill-caret ${isSegmentSelected ? "is-expanded" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSegmentClick(index)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSegmentClick(index);
                    }
                  }}
                />
              </div>

              {isSegmentSelected && (
                <div className="segment-pill-body">
                  <SegmentDetail
                    updateObstacleType={updateObstacleType}
                    updateDrawingMode={updateDrawingMode}
                    currentDrawingMode={currentDrawingMode}
                    setDeleteConfirmationVisible={setDeleteConfirmationVisible}
                    panelList={panelList}
                  />
                </div>
              )}
              </div>
            </React.Fragment>
          );
        })
      : null;

  const treeTypeCounts = { circle: 0, cone: 0 };
  const treeCards =
    trees.length > 0
      ? trees.map((tree, index) => {
          if (!tree) return null;
          const isTreeSelected = selectedTree === tree;
          const headerTextClass = theme ? "text-gray" : "text-gray-1";
          const foliageType = tree?.userData?.foliage?.geometry?.type || "";
          const treeTypeKey = foliageType.includes("Sphere") ? "circle" : "cone";
          const treeTypeLabel = treeTypeKey === "circle" ? "Sphere" : "Cone";
          treeTypeCounts[treeTypeKey] += 1;
          const treeTypeNumber = treeTypeCounts[treeTypeKey];

          return (
            <div
              key={tree.uuid || index}
              className={`segment-pill ui-card tree-pill ${theme ? "dark" : ""} ${
                isTreeSelected ? "is-expanded" : ""
              }`}
              >
                <Typography.Text
                  strong
                  className={`segment-pill-title ${headerTextClass}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isTreeSelected}
                onClick={() => handleTreeSelect(tree)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleTreeSelect(tree);
                  }
                  }}
                >
                  <span className="segment-pill-text">
                    <span className="tree-pill-chip" aria-hidden="true">
                      <span className="tree-pill-chip-icon" />
                    </span>
                    <span className="tree-pill-copy">
                      <span className="segment-pill-label">{`Tree ${treeTypeNumber}`}</span>
                      <span className="segment-pill-subtext">{treeTypeLabel}</span>
                    </span>
                  </span>
                </Typography.Text>

              <div className="segment-pill-actions">
                <Tooltip title={tree.userData?.locked ? "Unlock tree" : "Lock tree"}>
                  <Button
                    type="text"
                    size="small"
                    disabled={irradianceBusy}
                    icon={tree.userData?.locked ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleTreeLock(tree);
                    }}
                  />
                </Tooltip>
                <Button
                  danger
                  type="text"
                  size="small"
                  disabled={irradianceBusy}
                  icon={<img src={deleteButtonIcon} alt="Delete" className="delete-icon-img" />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteTree(tree);
                  }}
                />
                <img
                  src={chevronUpIcon}
                  alt="Toggle tree"
                  className={`segment-pill-caret ${isTreeSelected ? "is-expanded" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleTreeSelect(tree)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleTreeSelect(tree);
                    }
                  }}
                />
              </div>

              {isTreeSelected && (
                <div className="segment-pill-body">
                  <TreeEditorSegment
                    selectedTree={tree}
                    setTrees={setTrees}
                    irradianceBusy={irradianceBusy}
                  />
                </div>
              )}
            </div>
          );
        })
      : null;

  const isSegmentPanel = activePanel === "segments";
  const isTreePanel = activePanel === "trees";
  const sphereCount = removedSections?.spheres?.length || 0;

  return (
    <div className="segment-panel">
      <div className="segment__position" onClick={handleClick}>
        <div className="segment-stack">
          {isSegmentPanel ? (
            <SegmentNavigator
              updateDrawingMode={updateDrawingMode}
              currentDrawingMode={currentDrawingMode}
            />
          ) : trees.length > 0 ? (
            <div className="segment-toolbar tree-toolbar">
              <Dropdown overlay={treeMenu} overlayClassName="tree-menu-dropdown" placement="bottomRight" trigger={["click"]}>
                <Button
                  block
                  type="primary"
                  className="segment-add-button tree-add-button"
                  disabled={irradianceBusy}
                >
                  Add Tree <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          ) : null}

          <div className="segment-list">
            {isSegmentPanel ? (
              segmentCards
            ) : trees.length > 0 ? (
              treeCards
            ) : (
              <div className="segment-empty-state tree-empty-state">
                <div className="segment-empty-avatar">
                  <span className="segment-empty-icon tree-empty-icon" aria-hidden="true" />
                </div>
                <div className="segment-empty-title">No Trees Yet</div>
                <div className="segment-empty-subtitle">
                  Add Trees to calculate shadows
                </div>
                <Dropdown overlay={treeMenu} overlayClassName="tree-menu-dropdown" placement="bottomRight" trigger={["click"]}>
                  <Button
                    type="primary"
                    className="segment-add-button segment-empty-button tree-add-button"
                    disabled={irradianceBusy}
                  >
                    Add Tree
                  </Button>
                </Dropdown>
              </div>
            )}
          </div>

          {isTreePanel && sphereCount === 0 && (
            <div className="segment-empty-state tree-empty-state trim-empty-state">
              <div className="segment-empty-avatar">
                <img src={sphereIcon} alt="" className="sphere-empty-icon" aria-hidden="true" />
              </div>
              <div className="segment-empty-title">No Excluders Yet</div>
              <div className="segment-empty-subtitle">Add Excluder to calculate shadows</div>
              <Button
                type="primary"
                className="segment-add-button segment-empty-button"
                disabled={irradianceBusy}
                onClick={() => updateDrawingMode(DrawingModes.TRIM)}
              >
                Add Excluder
              </Button>
            </div>
          )}

          {isTreePanel && sphereCount > 0 && (
            <RemovedSections
              removedSections={removedSections}
              setRemovedSections={setRemovedSections}
              updateDrawingMode={updateDrawingMode}
              currentDrawingMode={currentDrawingMode}
              selectedSphereFromScene={selectedTrimSphere}
              irradianceBusy={irradianceBusy}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default Sections;
